import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import { MatSelectModule } from '@angular/material/select';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { ImpersonationApiService } from '../../services/impersonation-api.service';
import { ImpersonationService } from '../../services/impersonation.service';
import { ImpersonationStaffMember } from '../../models/impersonation.models';
import { CompanyContextService } from '../../../services/company-context.service';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../../shared/components/pagination-bar/pagination-bar.component';
import {
  handleImpersonationApiResult,
  notifyImpersonationFailure,
} from '../../utils/impersonation-ui.util';
import { ImpersonationConfirmModalComponent } from '../../../../shared/components/impersonation-confirm-modal';
import { ImpersonationEmptyStateComponent } from '../../../../shared/components/impersonation-empty-state';

/**
 * Staff impersonation for the company open in the detail shell. The company comes
 * from the route via CompanyContextService — this page no longer picks one.
 */
@Component({
  selector: 'app-impersonation-staff',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    PaginationBarComponent,
    ImpersonationConfirmModalComponent,
    ImpersonationEmptyStateComponent,
  ],
  templateUrl: './impersonation-staff.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./impersonation-staff.component.scss'],
})
export class ImpersonationStaffComponent implements OnInit, OnDestroy {
  private readonly api = inject(ImpersonationApiService);
  private readonly context = inject(CompanyContextService);
  private readonly impersonation = inject(ImpersonationService);
  private readonly messageService = inject(NotificationService);

  companyId = '';
  staff: ImpersonationStaffMember[] = [];
  loadingStaff = false;
  errorMessage = '';
  busyKey: string | null = null;

  confirmVisible = false;
  confirmMember: ImpersonationStaffMember | null = null;
  confirmUserId = '';

  searchTerm = '';
  empStatusFilter: 'all' | 'active' | 'inactive' = 'all';
  readonly empStatusOptions = [
    { label: 'All statuses', value: 'all' as const },
    { label: 'Active', value: 'active' as const },
    { label: 'Inactive', value: 'inactive' as const },
  ];

  currentPage = 1;
  itemsPerPage = 20;
  totalItems = 0;
  totalPages = 0;

  private readonly search$ = new Subject<string>();
  private searchSub?: Subscription;
  private contextSub?: Subscription;
  private staffRequestSub?: Subscription;

  ngOnInit(): void {
    this.contextSub = this.context.current$.subscribe(company => {
      const id = company?.id || '';
      if (id === this.companyId) {
        return;
      }
      this.companyId = id;
      this.resetFilters();
      this.loadStaff();
    });

    this.searchSub = this.search$.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1;
      this.loadStaff();
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.contextSub?.unsubscribe();
    this.staffRequestSub?.unsubscribe();
  }

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  companyName(): string {
    return this.context.name;
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;
    this.search$.next(value.trim());
  }

  onStatusFilterChange(): void {
    this.currentPage = 1;
    this.loadStaff();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadStaff();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.loadStaff();
  }

  loadStaff(): void {
    this.staffRequestSub?.unsubscribe();
    if (!this.companyId) {
      this.staff = [];
      this.loadingStaff = false;
      return;
    }

    this.loadingStaff = true;
    this.errorMessage = '';

    const params: {
      page: number;
      limit: number;
      search?: string;
      emp_status?: boolean;
    } = {
      page: this.currentPage,
      limit: this.itemsPerPage,
    };
    if (this.searchTerm.trim()) {
      params.search = this.searchTerm.trim();
    }
    if (this.empStatusFilter === 'active') {
      params.emp_status = true;
    } else if (this.empStatusFilter === 'inactive') {
      params.emp_status = false;
    }

    this.staffRequestSub = this.api.getShopStaff(this.companyId, params).subscribe({
      next: res => {
        this.loadingStaff = false;
        if (res.status && res.data) {
          this.staff = res.data.staff || [];
          this.totalItems = res.data.totalItems || 0;
          this.currentPage = res.data.currentPage || this.currentPage;
          this.totalPages = res.data.totalPages || 0;
          this.itemsPerPage = res.data.itemsPerPage || this.itemsPerPage;
          return;
        }
        this.staff = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.errorMessage = res.message || 'Unable to load staff.';
      },
      error: err => {
        this.loadingStaff = false;
        this.staff = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.errorMessage = err?.error?.message || err?.message || 'Failed to load staff.';
      },
    });
  }

  impersonate(member: ImpersonationStaffMember): void {
    if (!this.companyId) {
      return;
    }
    const userId = (member.user_id || member.id || '').trim();
    if (!userId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot impersonate',
        detail: 'This employee has no user id.',
        life: 5000,
      });
      return;
    }

    this.confirmMember = member;
    this.confirmUserId = userId;
    this.confirmVisible = true;
  }

  closeConfirm(): void {
    if (this.busyKey) {
      return;
    }
    this.confirmVisible = false;
    this.confirmMember = null;
    this.confirmUserId = '';
  }

  onConfirmImpersonate(): void {
    if (!this.confirmMember || !this.confirmUserId || !this.companyId || this.busyKey) {
      return;
    }
    this.startStaffImpersonation(this.confirmMember, this.confirmUserId);
  }

  private resetFilters(): void {
    this.staff = [];
    this.errorMessage = '';
    this.currentPage = 1;
    this.totalItems = 0;
    this.totalPages = 0;
    this.searchTerm = '';
    this.empStatusFilter = 'all';
  }

  private startStaffImpersonation(member: ImpersonationStaffMember, userId: string): void {
    this.busyKey = member.id;
    this.api
      .impersonateStaff({
        user_id: userId,
        company_id: this.companyId,
        reason: 'support',
      })
      .subscribe({
        next: res => {
          this.busyKey = null;
          handleImpersonationApiResult(
            res,
            data => {
              const started = this.impersonation.startFromStaffImpersonateApi(data, {
                company_id: this.companyId,
                company_name: this.context.name,
                timezone: this.context.timezone,
              });
              this.closeConfirmAfterStart();
              return started;
            },
            this.messageService,
            'No token returned from impersonate/staff.'
          );
        },
        error: err => {
          this.busyKey = null;
          notifyImpersonationFailure(
            this.messageService,
            err?.error?.message || err?.message || 'Request failed.'
          );
        },
      });
  }

  private closeConfirmAfterStart(): void {
    this.confirmVisible = false;
    this.confirmMember = null;
    this.confirmUserId = '';
  }
}
